import { observer } from "mobx-react";
import SettingStore from "../../../../../../stores/setting-store";
import Table from "../../../../../../components/table/table";
import TableRow from "../../../../../../components/table/components/table-row/table-row";
import useDashboardTranslation from "../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import Cell from "./components/cell/cell";
import ColumnTitle from "./components/column-title/column-title";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import { Input } from "../../../../../../components/inputs/input/input";
import { CUSTOMER_ACTIONS } from "../../../../../../models/User";
import ShipBackActionTableRow from "./components/ship-back-table-row/ship-back-table-row";
import { IValidationGroup } from "../../../../../../hooks/validation-group/validation-group-hook";
import './rma-cancellation-settings.scss';
import TranslationInput from "../../../../../../translation/translation-input/translation-input";

interface IProps {
    storeSetting: SettingStore;
    validationGroup: IValidationGroup;
}

const RMACancellationSettings: React.FC<IProps> = observer(({ storeSetting, validationGroup }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const chargedFeeInvoiceActionSetting = storeSetting.getInactiveRmasSetting(CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_CHARGED_FEES_INVOICE);
    const chargedExchangeOrderInvoiceActionSetting = storeSetting.getInactiveRmasSetting(CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_EXCHANGE_ORDER_INVOICE);

    return (
        <div className="rma-cancellation-settings">
        <Table
            columns={[
                { content: <ColumnTitle title={getTranslation('REQUIRED_CUSTOMER_ACTION', pageScope)} />, width: { value: 3, type: 'fr' } },
                { 
                    content: (
                        <ColumnTitle 
                            title={getTranslation('CANCEL_AFTER', pageScope)} 
                            tooltipText={getTranslation('CANCEL_AFTER_TOOLTIP_TEXT', pageScope)} 
                        />
                    ), 
                    width: { value: 2, type: 'fr'} 
                },
                { 
                    content: (
                        <ColumnTitle 
                            title={getTranslation('CANCELLATION_REASON', pageScope)} 
                            tooltipText={getTranslation('CANCELLATION_REASON_TOOLTIP_TEXT', pageScope)} 
                        />
                    ), 
                    width: { value: 5, type: 'fr'} 
                }
            ]}
        >
            <ShipBackActionTableRow key={1} validationGroup={validationGroup} storeSetting={storeSetting} />
            <TableRow
                skipWrapingCell
                key={2}
                cells={[
                    <Cell disabled={chargedFeeInvoiceActionSetting.cancelAfterDays === null}>
                        <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                            { getTranslation('PAY_THE_CHARGED_FEES_INVOICE', pageScope) }
                        </FontBody2>
                    </Cell>,
                    <Cell>
                        <>
                            <Input 
                                type={'number'}
                                value={chargedFeeInvoiceActionSetting.cancelAfterDays}
                                onChange={chargedFeeInvoiceActionSetting.setCancelAfterDays}
                                min={1}
                                max={999}
                                temp />
                            <FontBody2>{ getTranslation('DAYS', pageScope) }</FontBody2>
                        </>
                    </Cell>,
                    <Cell disabled={chargedFeeInvoiceActionSetting.cancelAfterDays === null}>
                        <div className="cancel-inactive-rma-reason-setting">
                            <TranslationInput 
                                translationKey={chargedFeeInvoiceActionSetting.reason}
                                validationGroup={validationGroup}
                                required={!!chargedFeeInvoiceActionSetting.cancelAfterDays}
                            />
                        </div>
                    </Cell>,
                ]}
            />
            <TableRow
                skipWrapingCell
                key={3}
                cells={[
                    <Cell disabled={chargedExchangeOrderInvoiceActionSetting.cancelAfterDays === null}>
                        <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                            { getTranslation('PAY_THE_EXCHANGE_ORDER_INVOICE', pageScope) }
                        </FontBody2>
                    </Cell>,
                    <Cell>
                        <>
                            <Input 
                                type={'number'}
                                value={chargedExchangeOrderInvoiceActionSetting.cancelAfterDays}
                                onChange={chargedExchangeOrderInvoiceActionSetting.setCancelAfterDays}
                                min={1}
                                max={999}
                                temp />
                            <FontBody2>{ getTranslation('DAYS', pageScope) }</FontBody2>
                        </>
                    </Cell>,
                    <Cell disabled={chargedExchangeOrderInvoiceActionSetting.cancelAfterDays === null}>
                        <div className="cancel-inactive-rma-reason-setting">
                            <TranslationInput 
                                translationKey={chargedExchangeOrderInvoiceActionSetting.reason}
                                validationGroup={validationGroup}
                                required={!!chargedExchangeOrderInvoiceActionSetting.cancelAfterDays}
                            />
                        </div>
                    </Cell>,
                ]}
            />           
        </Table>
        </div>
    );
});

export default RMACancellationSettings;